<script setup>
import AppLayout from "@/Layouts/AppLayout.vue";
import FormSection from "@/Components/FormSection.vue";
import InputLabel from "@/Components/InputLabel.vue";
import InputError from "@/Components/InputError.vue";
import TextInput from "@/Components/TextInput.vue";
import PrimaryButton from "@/Components/PrimaryButton.vue";
import SecondaryButton from "@/Components/SecondaryButton.vue";
import { useForm } from "@inertiajs/inertia-vue3";
import { computed } from "vue";
import { useTenantTextRules } from '@/helpers'

const props = defineProps({
    item: Object,
    type: String,
    requiredFields: {
        type: Array,
        default: () => [],
    },
});
const { resolveTextRule } = useTenantTextRules();

const fieldMap = {
    color: {
        colorName: "Color Name",
        cUnicode: "Unicode",
        hex: "Hex",
        pms: "PMS",
    },
    garment: {
        garmentName: "Garment Name",
        gdescription: "Description",
        gUnicode: "Unicode",
        imgURL: "Image URL",
    },
    fabric: {
        fabName: "Fabric Name",
        fabDescription: "Description",
        fUnicode: "Unicode",
        imgURL: "Image URL",
    },
    size: {
        scaleName: "Size Scale Name",
        opt1Name: "opt1Name",
        opt2Name: "opt2Name",
    },
    location: {
        name: "Location Name",
        identifier: "Identifier"
    },
    materialCode: {
        material_code: "Material Code"
    },
    department: {
        department: "Department Name"
    },
    styleNumber: {
        number: "Style Number"
    }
}

const defaultTabLabels = {
    color: 'Color',
    garment: 'Garment',
    fabric: 'Fabric',
    size: 'Size Scale',
    location: 'Location',
    materialCode: 'Material Code',
    department: 'Department',
    styleNumber: 'Style',
};

const textRuleKeyByTab = {
    color: 'database.tabs.color',
    garment: 'database.tabs.garment',
    fabric: 'database.tabs.fabric',
    size: 'database.tabs.size',
    location: 'database.tabs.location',
    materialCode: 'database.tabs.material_code',
    department: 'database.tabs.department',
    styleNumber: 'database.tabs.style_number',
};

const formatTabLabel = (tab) => {
    if (!tab) return '';

    const fallback = defaultTabLabels[tab]
        || tab.replace(/([A-Z])/g, ' $1').replace(/^./, (m) => m.toUpperCase());
    const ruleKey = textRuleKeyByTab[tab];

    if (!ruleKey) {
        return fallback;
    }

    return resolveTextRule(ruleKey, fallback, ruleKey);
};

const garmentLabel = computed(() => resolveTextRule('garment.label', 'Garment', 'database.edit.garment.label'));
const fabricLabel = computed(() => resolveTextRule('fabric.label', 'Fabric', 'database.edit.fabric.label'));
const sizeScaleLabel = computed(() => formatTabLabel('size'));
const locationLabel = computed(() => resolveTextRule('location.label', 'Location', 'database.edit.location.label'));
const styleNumberLabel = computed(() => resolveTextRule('style_number.label', 'Style Number', 'database.edit.style_number.label'));
const materialCodeLabel = computed(() => formatTabLabel('materialCode'));
const departmentLabel = computed(() => formatTabLabel('department'));

console.log('Props:', props);

const fieldsForType = computed(() => {
    const type = props.type;
    const fields = { ...(fieldMap[type] || {}) };

    if (type === 'garment') {
        fields.garmentName = `${garmentLabel.value} Name`;
    }

    if (type === 'fabric') {
        fields.fabName = `${fabricLabel.value} Name`;
    }

    if (type === 'size') {
        fields.scaleName = `${sizeScaleLabel.value} Name`;
    }

    if (type === 'location') {
        fields.name = `${locationLabel.value} Name`;
    }

    if (type === 'materialCode') {
        fields.material_code = materialCodeLabel.value;
    }

    if (type === 'department') {
        fields.department = `${departmentLabel.value} Name`;
    }

    if (type === 'styleNumber') {
        fields.number = styleNumberLabel.value;
    }

    return fields;
});

console.log('Fields for type:', fieldsForType.value);

const backRoute = computed(() => {
    const url = route('admin.database.index');
    return `${url}?tab=${props.type}`;
});

const getItemId = (item) => {
    const idKey = Object.keys(item).find(key => key.toLowerCase().endsWith('id'))
    return item[idKey]
}

const id = getItemId(props.item);

const form = useForm({
    _method: id ? 'PUT' : 'POST',
    ...props.item
});


const saveForm = () => {
    const routeName = id
        ? route('admin.database.update', { id: id, type: props.type })
        : route('admin.database.store', { type: props.type })

    form.post(routeName, {
        preserveScroll: true
    });
};

const visibleKeys = computed(() => {
    return Object.keys(props.item).filter(key => !key.toLowerCase().endsWith('id'));
});

const isRequiredField = (key) => props.requiredFields.includes(key);
</script>

<template>
    <AppLayout :title="`Edit ${formatTabLabel(props.type)}`">
        <template #header>
            <h2 class="font-semibold text-xl text-gray-800 leading-tight">Edit {{ formatTabLabel(props.type) }}</h2>
        </template>

        <div class="max-w-2xl mx-auto py-10 sm:px-6 lg:px-8">
            <FormSection gridClasses="grid-cols-1" :noGrid="true">
                <template #title>
                    <a :href="backRoute">
                        <SecondaryButton>Back</SecondaryButton>
                    </a>
                </template>

                <template #form>
                    <div class="flex flex-col gap-4">
                        <div v-for="(label, key) in fieldsForType" :key="key">
                            <InputLabel :value="label" :required="isRequiredField(key)" />
                            <TextInput
                                v-model="form[key]"
                                class="w-full mt-1"
                                :required="isRequiredField(key)"
                                :aria-required="isRequiredField(key)"
                            />
                            <InputError :message="form.errors[key]" class="mt-2" />
                        </div>

                    </div>
                </template>

                <template #actions>
                    <PrimaryButton @click.prevent="saveForm">Save</PrimaryButton>
                </template>
            </FormSection>
        </div>
    </AppLayout>
</template>
